/**
 * CHA tier presentation -- one map, keyed on the generated CollateralHealthTier
 * union (schema-registry/collateral-policy.openapi.yaml), so a tier added to
 * the spec fails tsc here until it has a label and a color.
 *
 * Before this module the policy editors carried two copies of the map and both
 * gave YELLOW and ORANGE the same `warning` token, so the two tiers read as
 * one color on the threshold bar, the tier cards and the field labels (UI run
 * 2026-09-27, fi-g7-6). The preset carries a distinct `warning-bright` token,
 * which ORANGE now uses.
 *
 * The pledge lists' tier badge (pages/pledges/pledge-display.tsx ChaTierBadge)
 * carried a third copy with the same collapse; it now reads this map too.
 */
import type { components } from '@aaimplatform/institutional-types/generated/collateral-policy';

export type CollateralHealthTier = components['schemas']['CollateralHealthTier'];

export interface ChaTierClassConfig {
  /** Text color for the tier's name and its field labels. */
  text: string;
  /** Solid fill for the tier's band on a threshold bar. */
  bar: string;
  /** Tinted panel background for a tier reference card. */
  panel: string;
  /** Border for a tier reference card. */
  border: string;
}

export const CHA_TIER_CLASSES: Record<CollateralHealthTier, ChaTierClassConfig> = {
  GREEN: { text: 'text-growth', bar: 'bg-growth', panel: 'bg-growth/10', border: 'border-growth/30' },
  YELLOW: { text: 'text-warning', bar: 'bg-warning', panel: 'bg-warning/10', border: 'border-warning/30' },
  ORANGE: {
    text: 'text-warning-bright',
    bar: 'bg-warning-bright',
    panel: 'bg-warning-bright/10',
    border: 'border-warning-bright/30',
  },
  RED: { text: 'text-error', bar: 'bg-error', panel: 'bg-error/10', border: 'border-error/30' },
};

/** Display names for the tiers; the enum codes never print as copy. */
export const CHA_TIER_LABELS: Record<CollateralHealthTier, string> = {
  GREEN: 'Green',
  YELLOW: 'Yellow',
  ORANGE: 'Orange',
  RED: 'Red',
};

export const CHA_TIERS: ReadonlyArray<CollateralHealthTier> = ['GREEN', 'YELLOW', 'ORANGE', 'RED'];
